import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { 
  ArrowLeft, 
  DollarSign, 
  Plus, 
  Trash2, 
  AlertCircle, 
  Loader2,
  Copy,
  Download,
  Upload,
  FileText,
  File,
  CheckCircle2,
  UserCog,
  Calendar,
  Pencil,
  FileDown
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { createPageUrl } from "@/utils";
import { uploadFile } from "@/integrations/Core";
import { jsPDF } from "jspdf";
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';

export default function RendimentoPessoaFisica() {
  // Refs para os inputs de arquivo
  const fileInputRefs = {
    janeiro: useRef(null),
    fevereiro: useRef(null),
    marco: useRef(null),
    abril: useRef(null),
    maio: useRef(null),
    junho: useRef(null),
    julho: useRef(null),
    agosto: useRef(null),
    setembro: useRef(null),
    outubro: useRef(null),
    novembro: useRef(null),
    dezembro: useRef(null)
  };
  
  // Ref para o input de arquivo de CPFs corrigidos
  const cpfsCorrectedFileInputRef = useRef(null);
  
  // Estados para controle de arquivos e processamento (armazena um array de arquivos por mês)
  const [filesByMonth, setFilesByMonth] = useState({
    janeiro: [],
    fevereiro: [],
    marco: [],
    abril: [],
    maio: [],
    junho: [],
    julho: [],
    agosto: [],
    setembro: [],
    outubro: [],
    novembro: [],
    dezembro: []
  });
  
  // Estado para armazenar o arquivo de CPFs corrigidos
  const [cpfsCorrectedFile, setCpfsCorrectedFile] = useState(null);
  
  // Estado para controlar se está processando CPFs corrigidos
  const [processingCorrected, setProcessingCorrected] = useState(false);
  
  // Estado para armazenar CPFs que ainda estão inválidos após correção manual
  const [stillInvalidCpfs, setStillInvalidCpfs] = useState([]);
  
  // Estado para controlar a edição de CPFs inválidos
  const [editingCpfs, setEditingCpfs] = useState(false);
  const [editedCpfs, setEditedCpfs] = useState({});
  
  // Estado para controlar a edição de valores
  const [editingValuesMonth, setEditingValuesMonth] = useState(null); // Armazena qual mês está sendo editado
  const [editedValues, setEditedValues] = useState({});
  const [processingEditedValues, setProcessingEditedValues] = useState(false);
  
  const [dragActiveMonth, setDragActiveMonth] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [processedDocuments, setProcessedDocuments] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [processingDetails, setProcessingDetails] = useState({});
  const [logs, setLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' ou 'resultado'

  // Mapeamento de meses
  const months = {
    janeiro: { name: "JAN", color: "blue" },
    fevereiro: { name: "FEV", color: "indigo" },
    marco: { name: "MAR", color: "violet" },
    abril: { name: "ABR", color: "purple" },
    maio: { name: "MAI", color: "pink" },
    junho: { name: "JUN", color: "rose" },
    julho: { name: "JUL", color: "red" },
    agosto: { name: "AGO", color: "orange" },
    setembro: { name: "SET", color: "amber" },
    outubro: { name: "OUT", color: "yellow" },
    novembro: { name: "NOV", color: "lime" },
    dezembro: { name: "DEZ", color: "green" }
  };

  // Função para salvar log na pasta /logs
  const saveLog = async (action, data, response = null, error = null) => {
    try {
      const timestamp = new Date().toISOString();
      const logEntry = {
        timestamp,
        action,
        data,
        response,
        error: error ? error.toString() : null
      };
      
      // Adicionar ao estado de logs para exibição na UI (opcional)
      setLogs(prev => [...prev, logEntry]);
      
      // Enviar log para o servidor - formato antigo compatível com log.php
      const logData = {
        type: "DEBUG",
        message: `${action}: ${JSON.stringify(data).substring(0, 100)}...`,
        data: {
          action,
          details: data,
          response: response ? response : null,
          error: error ? error.toString() : null,
          timestamp
        }
      };
      
      console.log("Enviando log para servidor:", logData);
      
      // Fazer requisição para o endpoint de log (PHP)
      try {
        const logResponse = await fetch('/api/log.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(logData)
        });
        
        if (!logResponse.ok) {
          console.error('Erro ao salvar log no servidor:', await logResponse.text());
        } else {
          console.log(`Log salvo com sucesso: ${action}`);
        }
      } catch (serverLogError) {
        console.error('Exceção ao tentar salvar log no servidor:', serverLogError);
      }
    } catch (logError) {
      console.error('Erro ao preparar log:', logError);
    }
  };

  // Função para lidar com eventos de arrastar arquivos
  const handleDrag = (e, month) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActiveMonth(month);
    } else if (e.type === "dragleave") {
      setDragActiveMonth(null);
    }
  };

  // Função para lidar com soltar arquivos
  const handleDrop = async (e, month) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveMonth(null);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      // Pegar apenas o primeiro arquivo
      const file = e.dataTransfer.files[0];
      
      // Gerar um ID único para o arquivo (para facilitar a remoção depois)
      const fileId = `${Date.now()}-${file.name}`;
      const fileWithId = Object.assign(file, { id: fileId });
      
      // Atualizar o estado adicionando o novo arquivo ao array do mês
      setFilesByMonth(prev => ({
        ...prev,
        [month]: [...prev[month], fileWithId]
      }));
      
      // Iniciar upload automaticamente
      try {
        await uploadSingleFile(fileWithId, month);
      } catch (error) {
        console.error(`Erro ao fazer upload do arquivo para ${months[month].name}:`, error);
        setError(`Erro ao fazer upload do arquivo para ${months[month].name}: ${error.message}`);
        
        // Remover o arquivo da lista se o upload falhar
        setFilesByMonth(prev => ({
          ...prev,
          [month]: prev[month].filter(f => f.id !== fileId)
        }));
      }
    }
  };

  // Função para lidar com seleção de arquivos pelo input
  const handleFileInput = async (e, month) => {
    if (e.target.files && e.target.files.length > 0) {
      // Processar todos os arquivos selecionados
      const filesArray = Array.from(e.target.files);
      const timestamp = Date.now(); // Usar mesmo timestamp para manter ordem de seleção
      
      // Preparar novos arquivos com IDs
      const newFiles = filesArray.map((file, index) => {
        const fileId = `${timestamp + index}-${file.name}`;
        return Object.assign(file, { id: fileId });
      });
      
      // Atualizar o estado adicionando os novos arquivos ao array do mês
      setFilesByMonth(prev => ({
        ...prev,
        [month]: [...prev[month], ...newFiles]
      }));
      
      // Iniciar upload para cada arquivo selecionado
      for (const fileWithId of newFiles) {
        try {
          await uploadSingleFile(fileWithId, month);
        } catch (error) {
          console.error(`Erro ao fazer upload do arquivo ${fileWithId.name} para ${months[month].name}:`, error);
          setError(`Erro ao fazer upload do arquivo ${fileWithId.name} para ${months[month].name}: ${error.message}`);
          
          // Remover apenas este arquivo da lista se o upload falhar
          setFilesByMonth(prev => ({
            ...prev,
            [month]: prev[month].filter(f => f.id !== fileWithId.id)
          }));
        }
      }
    }
  };

  // Função para abrir o seletor de arquivos
  const handleButtonClick = (month) => {
    fileInputRefs[month]?.current?.click();
  };

  // Função para remover um arquivo específico por ID
  const removeFile = (month, fileId) => {
    // Remover o arquivo específico do array do mês
    setFilesByMonth(prev => ({
      ...prev,
      [month]: prev[month].filter(file => file.id !== fileId)
    }));
    
    // Limpar o progresso de upload para este arquivo específico
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileId];
      return newProgress;
    });
  };
  
  // Função para remover todos os arquivos de um mês
  const removeAllFilesFromMonth = (month) => {
    // Limpar todos os arquivos do mês
    setFilesByMonth(prev => ({
      ...prev,
      [month]: []
    }));
    
    // Remover o progresso de upload para todos os arquivos deste mês
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      // Remover qualquer entrada que comece com o nome do mês
      Object.keys(newProgress).forEach(key => {
        if (key.startsWith(`${month}-`) || key === month) {
          delete newProgress[key];
        }
      });
      return newProgress;
    });
  };

  // Função para fazer upload de um arquivo
  const uploadSingleFile = async (file, month) => {
    try {
      const fileId = file.id || `${month}-${Date.now()}-${file.name}`;
      
      // Se o arquivo já não tiver um ID, adicionar um
      if (!file.id) {
        file.id = fileId;
      }
      
      // Inicializar progresso usando o ID do arquivo como chave
      setUploadProgress(prev => ({
        ...prev,
        [fileId]: 0
      }));
      
      // Registrar início do upload
      await saveLog('INICIO_UPLOAD', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        month,
        fileId
      });
      
      // Fazer upload usando a função importada
      // Importante: A função uploadFile espera receber o arquivo diretamente, não um FormData
      const uploadResult = await uploadFile(file, (progress) => {
        // Atualizar progresso usando o ID do arquivo como chave
        setUploadProgress(prev => ({
          ...prev,
          [fileId]: progress
        }));
      });
      
      // Adicionar o ID do arquivo e o mês ao resultado do upload
      uploadResult.fileId = fileId;
      uploadResult.month = month;
      
      // Registrar sucesso do upload
      await saveLog('UPLOAD_SUCESSO', {
        fileName: file.name,
        month,
        fileId,
        response: uploadResult
      });
      
      // Finalizar progresso
      setUploadProgress(prev => ({
        ...prev,
        [fileId]: 100
      }));
      
      return uploadResult;
    } catch (error) {
      // Registrar erro do upload
      await saveLog('ERRO_UPLOAD', {
        fileName: file.name,
        month,
        fileId: file.id,
        error: error.toString()
      }, null, error);
      
      throw error;
    }
  };

  // Função para processar todos os arquivos
  const processAllDocuments = async () => {
    // Verificar se há pelo menos um arquivo para processar (em qualquer mês)
    const hasFiles = Object.values(filesByMonth).some(files => files.length > 0);
    
    if (!hasFiles) {
      setError("Nenhum arquivo selecionado para processamento.");
      return;
    }
    
    setProcessing(true);
    setError(null);
    setResult(null);
    setProcessedDocuments([]);
    
    try {
      // Registrar início do processamento em lote
      await saveLog('INICIO_PROCESSAMENTO_LOTE', {
        meses: Object.entries(filesByMonth)
          .filter(([_, files]) => files.length > 0)
          .map(([month, _]) => month)
      });
      
      // Array para armazenar resultados de upload
      const uploadResults = [];
      
      // Para cada mês que tenha arquivos
      for (const [month, files] of Object.entries(filesByMonth)) {
        // Pular mês sem arquivos
        if (files.length === 0) continue;
        
        // Para cada arquivo no mês
        for (const file of files) {
          const fileId = file.id;
          
          // Se o arquivo não foi enviado completamente (progresso < 100), tente enviá-lo agora
          if (!uploadProgress[fileId] || uploadProgress[fileId] !== 100) {
            try {
              const uploadResult = await uploadSingleFile(file, month);
              uploadResults.push({
                month,
                file,
                result: uploadResult,
                success: true,
                fileId
              });
            } catch (error) {
              console.error(`Erro ao fazer upload do arquivo ${file.name} para ${months[month].name}:`, error);
              uploadResults.push({
                month,
                file,
                error,
                success: false,
                fileId
              });
            }
          } else {
            // O arquivo já foi enviado, obtenha a URL do resultado anterior
            const fileName = file.name;
            const baseUrl = window.location.origin;
            const url = `${baseUrl}/files/${fileName}`;
            
            uploadResults.push({
              month,
              file,
              result: {
                success: true,
                url,
                full_url: url,
                file_name: fileName,
                fileId
              },
              success: true,
              fileId
            });
          }
        }
      }
      
      // Verificar se algum upload falhou
      const failedUploads = uploadResults.filter(r => !r.success);
      if (failedUploads.length > 0) {
        const errorMessage = `Falha no upload de ${failedUploads.length} arquivo(s): ${failedUploads.map(f => f.file.name).join(', ')}`;
        setError(errorMessage);
        await saveLog('ERRO_UPLOAD_MULTIPLO', {
          falhas: failedUploads.map(f => ({
            month: f.month,
            fileName: f.file.name,
            error: f.error?.toString()
          }))
        });
        setProcessing(false);
        return;
      }
      
      // Iniciar processamento dos documentos
      setProcessingDetails({ geral: 'Iniciando processamento dos documentos...' });
      
      // Estrutura para armazenar resultados do processamento por mês
      const processedResults = {};
      const cpfsInvalidosTodos = [];
      
      // Map para armazenar dados processados temporariamente por mês
      const processedDataByMonth = {};
      
      // Para cada arquivo com upload bem-sucedido
      for (const uploadResult of uploadResults) {
        const { month, file, result, fileId } = uploadResult;
        
        try {
          // Atualizar detalhes de processamento
          setProcessingDetails(prev => ({
            ...prev,
            [fileId]: `Processando arquivo ${file.name} de ${months[month].name}...`
          }));
          
          // Extrair informações do documento via OCR
          const contentOCR = await extractDocumentInfo(result.full_url, month, file.name);
          
          // Processar os dados extraídos
          const dadosProcessados = await processarDadosExtraidos(contentOCR, month, uploadResults);
          
          // Log do resultado do processamento
          await saveLog('PROCESSAMENTO_RENDIMENTO_SUCESSO', {
            month,
            fileName: file.name,
            fileId,
            cpfsValidos: dadosProcessados.rendimentos.length,
            cpfsInvalidos: dadosProcessados.cpfsInvalidos.length
          });
          
          // Inicializar array para o mês se não existir
          if (!processedDataByMonth[month]) {
            processedDataByMonth[month] = {
              rendimentos: [],
              cpfsInvalidos: [],
              arquivosProcessados: []
            };
          }
          
          // Adicionar os rendimentos ao array do mês
          processedDataByMonth[month].rendimentos.push(...dadosProcessados.rendimentos);
          
          // Adicionar CPFs inválidos ao array do mês
          processedDataByMonth[month].cpfsInvalidos.push(...dadosProcessados.cpfsInvalidos);
          
          // Adicionar informações do arquivo processado
          processedDataByMonth[month].arquivosProcessados.push({
            fileName: file.name,
            fileId,
            totalCPFs: dadosProcessados.rendimentos.length,
            cpfsInvalidos: dadosProcessados.cpfsInvalidos.length,
            valorTotal: dadosProcessados.rendimentos.reduce((acc, item) => acc + item.valor, 0)
          });
          
          // Atualizar estado de documentos processados
          setProcessedDocuments(prev => [
            ...prev,
            {
              month,
              fileName: file.name,
              fileId,
              status: 'concluido',
              data: {
                mes: months[month].name,
                totalCPFs: dadosProcessados.rendimentos.length,
                cpfsInvalidos: dadosProcessados.cpfsInvalidos.length,
                valorTotal: dadosProcessados.rendimentos.reduce((acc, item) => acc + item.valor, 0).toFixed(2)
              }
            }
          ]);
          
          // Atualizar detalhes de processamento
          setProcessingDetails(prev => ({
            ...prev, 
            [fileId]: `Arquivo ${file.name} de ${months[month].name} processado com sucesso!`
          }));
          
        } catch (error) {
          console.error(`Erro ao processar arquivo ${file.name} de ${months[month].name}:`, error);
          
          // Registrar erro
          await saveLog('ERRO_PROCESSAMENTO_DOCUMENTO', {
            month,
            fileName: file.name,
            fileId,
            error: error.toString()
          }, null, error);
          
          // Atualizar estado de documentos processados
          setProcessedDocuments(prev => [
            ...prev,
            {
              month,
              fileName: file.name,
              fileId,
              status: 'erro',
              mensagem: error.toString()
            }
          ]);
          
          // Atualizar detalhes de processamento
          setProcessingDetails(prev => ({
            ...prev,
            [fileId]: `Erro ao processar arquivo ${file.name} de ${months[month].name}: ${error.toString()}`
          }));
        }
      }
      
      // Processar dados por mês após processar todos os arquivos
      for (const [month, data] of Object.entries(processedDataByMonth)) {
        // Verificar se há rendimentos para processar
        if (data.rendimentos.length === 0) continue;
        
        // Não agregar valores por CPF - manter cada recibo como uma entrada individual
        // Armazenar diretamente os rendimentos individuais no resultado final sem arquivosProcessados
        processedResults[month] = {
          rendimentos: data.rendimentos
        };
        
        // Acumular todos os CPFs inválidos
        if (data.cpfsInvalidos.length > 0) {
          cpfsInvalidosTodos.push(...data.cpfsInvalidos);
        }
      }
      
      // Criar resultado final para exportação (sem cpfsInvalidos)
      const finalResult = {
        rendimentos: processedResults
      };
      
      // Atualizar estado com resultado completo (incluindo cpfsInvalidos para uso interno)
      // Garantir que o campo arquivosProcessados seja removido de cada mês
      const cleanedResults = {};
      
      // Criar uma cópia limpa dos resultados sem o campo arquivosProcessados
      for (const [month, data] of Object.entries(processedResults)) {
        cleanedResults[month] = {
          rendimentos: data.rendimentos
        };
      }
      
      setResult({
        rendimentos: cleanedResults,
        cpfsInvalidos: cpfsInvalidosTodos,
        timestamp: new Date().toISOString()
      });
      
      // Registrar conclusão do processamento
      await saveLog('PROCESSAMENTO_CONCLUIDO', {
        resultado: {
          rendimentos: processedResults,
          cpfsInvalidos: cpfsInvalidosTodos,
          timestamp: new Date().toISOString()
        }
      });
      
      // Limpar detalhes de processamento
      setTimeout(() => {
        setProcessingDetails({});
        // Mudar para a aba de resultado
        setActiveTab('resultado');
      }, 3000);
      
    } catch (error) {
      console.error('Erro geral no processamento:', error);
      setError(`Erro no processamento: ${error.toString()}`);
      
      // Registrar erro geral
      await saveLog('ERRO_GERAL_PROCESSAMENTO', {
        error: error.toString()
      }, null, error);
    } finally {
      setProcessing(false);
    }
  };

  // Função para copiar o resultado para a área de transferência
  const copyResultToClipboard = () => {
    if (!result) return;
    
    // Garantir que o resultado contenha apenas a estrutura esperada (sem arquivosProcessados)
    const resultToExport = { 
      rendimentos: Object.fromEntries(
        Object.entries(result.rendimentos).map(([month, data]) => [
          month, 
          { rendimentos: data.rendimentos }
        ])
      )
    };
    const resultString = JSON.stringify(resultToExport, null, 2);
    navigator.clipboard.writeText(resultString);
    alert("JSON copiado para a área de transferência!");
  };
  
  // Função para formatar um valor como monetário no formato brasileiro (R$ 1.234,56)
  const formatarValorMonetario = (valor) => {
    if (valor === null || valor === undefined || isNaN(valor)) {
      return '0,00';
    }
    
    // Garantir que o valor seja tratado como número
    const valorNumerico = typeof valor === 'string' ? parseFloat(valor.replace(',', '.')) : valor;
    
    // Formatar o número com separadores de milhares e decimais no formato brasileiro
    return valorNumerico.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Função para baixar o resultado do processamento como PDF usando jsPDF
const downloadResultAsPDF = () => {
  try {
    if (!result) {
      console.error('Nenhum resultado disponível para gerar PDF');
      alert('Nenhum resultado disponível para gerar o PDF.');
      return;
    }
    
    console.log('Iniciando geração de PDF...');
    console.log('Dados disponíveis:', JSON.stringify(result).substring(0, 100) + '...');
    
    // Calcular o valor total anual
    let valorTotalAnual = 0;
    Object.values(result.rendimentos).forEach(data => {
      if (data.rendimentos && data.rendimentos.length > 0) {
        data.rendimentos.forEach(item => {
          valorTotalAnual += typeof item.valor === 'number' ? item.valor : 0;
        });
      }
    });
    
    // Inicializar o documento PDF (orientação portrait, unidade em mm, tamanho A4)
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    // Configurações das cores para os meses
    const monthColors = {
      janeiro: [59, 130, 246],     // blue
      fevereiro: [99, 102, 241],   // indigo
      marco: [139, 92, 246],       // violet
      abril: [168, 85, 247],       // purple
      maio: [236, 72, 153],        // pink
      junho: [244, 63, 94],        // rose
      julho: [239, 68, 68],        // red
      agosto: [249, 115, 22],      // orange
      setembro: [245, 158, 11],    // amber
      outubro: [234, 179, 8],      // yellow
      novembro: [132, 204, 22],    // lime
      dezembro: [34, 197, 94]      // green
    };
    
    // Adicionar título do relatório
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(51, 51, 51);
    doc.text('Relatório de Rendimentos Pessoa Física', doc.internal.pageSize.getWidth() / 2, 20, { align: 'center' });
    
    // Adicionar data de geração
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(102, 102, 102);
    const dataHora = new Date().toLocaleString('pt-BR', { 
      dateStyle: 'short', 
      timeStyle: 'medium'
    });
    doc.text(`Gerado em: ${dataHora}`, doc.internal.pageSize.getWidth() / 2, 28, { align: 'center' });
    
    let yPosition = 40; // Posição vertical inicial para os conteúdos
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    
    // Total de CPFs em todos os meses
    let totalCPFsAnual = 0;
    Object.values(result.rendimentos).forEach(data => {
      if (data.rendimentos && data.rendimentos.length > 0) {
        totalCPFsAnual += data.rendimentos.length;
      }
    });
    
    // Processar cada mês com rendimentos
    Object.entries(result.rendimentos).forEach(([month, data], index) => {
      if (data.rendimentos && data.rendimentos.length > 0) {
        console.log(`Processando mês ${month} com ${data.rendimentos.length} CPFs`);
        
        // Verificar se é necessário adicionar uma nova página
        if (yPosition > 250 && index > 0) {
          doc.addPage();
          yPosition = 40;
        }
        
        // Definir cores para o mês atual
        const color = monthColors[month] || [59, 130, 246]; // Azul como padrão
        
        // Altura do conteúdo depende do número de CPFs
        const contentHeight = Math.min(35 + (data.rendimentos.length * 7), 120);
        
        // Título do mês (abreviação em 3 letras em maiúsculas)
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(color[0], color[1], color[2]);
        // Usar a mesma indentação horizontal que é usada para 'Total de CPFs' e 'Valor Total'
        doc.text(months[month].name, margin + 6, yPosition + 10);
        
        // Total de CPFs
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(51, 51, 51);
        doc.text(`Total de CPFs: ${data.rendimentos.length}`, margin + 6, yPosition + 18);
        
        // Calcular valor total
        const valorTotal = data.rendimentos.reduce(
          (acc, item) => acc + (typeof item.valor === 'number' ? item.valor : 0), 
          0
        );
        
        // Valor total
        doc.setFont('helvetica', 'bold');
        doc.text(`Valor Total: R$ ${formatarValorMonetario(valorTotal)}`, margin + 6, yPosition + 26);
        
        // Tabela de CPFs e valores
        const tableStart = yPosition + 30;
        
        // Cabeçalho da tabela CPFs
        const tableData = data.rendimentos.map(item => [
          item.cpf,
          `R$ ${formatarValorMonetario(item.valor)}`
        ]);
        
        // Definir estilos da tabela
        autoTable(doc, {
          startY: tableStart,
          margin: { left: margin + 4, right: margin + 4 },
          head: [['CPF', 'Valor']],
          body: tableData,
          theme: 'plain',
          styles: {
            fontSize: 10,
            cellPadding: 2,
            lineColor: [240, 240, 240],
            lineWidth: 0.1,
          },
          columnStyles: {
            0: { fontStyle: 'normal', halign: 'left' },
            1: { fontStyle: 'bold', halign: 'right' }
          },
          headStyles: {
            fillColor: [245, 245, 245],
            textColor: [51, 51, 51],
            fontStyle: 'bold'
          },
          willDrawCell: function(data) {
            // Apenas desenhar borda inferior para células do corpo
            if (data.section === 'body') {
              doc.setDrawColor(240, 240, 240);
              doc.setLineWidth(0.1);
            }
          }
        });
        
        // Atualizar posição Y para o próximo card
        // Obter a posição final da tabela
        const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY : tableStart;
        yPosition = finalY + 15;
      }
    });
    
    // Adicionar seção de valor total anual
    // Verificar se é necessário adicionar uma nova página
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 40;
    }
    
    // Adicionar linha divisória antes do resumo anual
    doc.setDrawColor(100, 100, 100);
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 15;
    
    // Título da seção Resumo Anual
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(51, 51, 51);
    doc.text('RESUMO ANUAL', margin + 6, yPosition);
    yPosition += 15;
    
    // Total de CPFs processados
    doc.setFontSize(12);
    doc.text(`Total de CPFs processados: ${totalCPFsAnual}`, margin + 6, yPosition);
    yPosition += 10;
    
    // Valor total anual
    doc.setFontSize(14);
    doc.setTextColor(0, 128, 0); // Verde para o valor total anual
    doc.text(`VALOR TOTAL ANUAL: R$ ${formatarValorMonetario(valorTotalAnual)}`, margin + 6, yPosition);
    yPosition += 20;
    
    // Adicionar seção de CPFs inválidos se existirem
    if (result.cpfsInvalidos && result.cpfsInvalidos.length > 0) {
      // Verificar se é necessário adicionar uma nova página
      if (yPosition > 220) {
        doc.addPage();
        yPosition = 40;
      }
      
      // Título da seção
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(244, 67, 54); // Vermelho para CPFs inválidos
      doc.text(`CPFs Inválidos (${result.cpfsInvalidos.length})`, margin, yPosition);
      
      // Desenhar linha divisória
      doc.setDrawColor(244, 67, 54);
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition + 5, pageWidth - margin, yPosition + 5);
      
      // Criar tabela de CPFs inválidos
      const invalidData = result.cpfsInvalidos.map(item => [
        item.cpf || 'Sem CPF',
        item.valor ? `R$ ${item.valor}` : 'N/A',
        item.mes || 'Não especificado'
      ]);
      
      autoTable(doc, {
        startY: yPosition + 10,
        margin: { left: margin, right: margin },
        head: [['CPF', 'Valor', 'Mês']],
        body: invalidData,
        theme: 'striped',
        styles: {
          fontSize: 10,
          cellPadding: 3
        },
        headStyles: {
          fillColor: [244, 67, 54], // Vermelho
          textColor: [255, 255, 255]
        },
        alternateRowStyles: {
          fillColor: [255, 235, 238], // Vermelho claro
        }
      });
    }
    
    console.log('PDF gerado com sucesso, salvando...');
    // Salvar o arquivo PDF
    doc.save('RendimentosPF.pdf');
    console.log('PDF salvo com sucesso!');
  } catch (error) {
    console.error('Erro ao gerar PDF:', error);
    alert(`Erro ao gerar o PDF: ${error.message}. Verifique o console para mais detalhes.`);
  }
};

// Função para baixar o JSON como arquivo
const downloadJsonFile = () => {
  if (!result) return;
  
  // Garantir que o resultado contenha apenas a estrutura esperada (sem arquivosProcessados)
  const resultToExport = { 
    rendimentos: Object.fromEntries(
      Object.entries(result.rendimentos).map(([month, data]) => [
        month, 
        { rendimentos: data.rendimentos }
      ])
    )
  };
  const resultString = JSON.stringify(resultToExport, null, 2);
  const blob = new Blob([resultString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'RendimentosPF.json';
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(a);
};  

  // Função para extrair informações do documento via API
  const extractDocumentInfo = async (url, month, fileName, retryCount = 0) => {
    try {
      // Verificar se a URL é válida
      if (!url || url === 'undefined') {
        throw new Error(`URL inválida para o documento de ${months[month].name}`);
      }
      
      // Incluir timestamp na URL para evitar cache (apenas para o log)
      const noCacheUrl = `${url}?nocache=${new Date().getTime()}`;
      
      setProcessingDetails(prev => ({
        ...prev, 
        [month]: `Enviando documento de ${months[month].name} para o OCR... ${retryCount > 0 ? `(Tentativa ${retryCount + 1}/3)` : ''}`
      }));
      
      // Endpoint do OCR na Replit
      const endpoint = 'https://document-ocr-extractor-contatoarthurol.replit.app/process-document';
      
      // Modelo para usar na extração
      const model = 'meta-llama/Llama-4-Maverick-17B-128E-Instruct-FP8';
      
      // Prompt para extração de informações
      const prompt = "**Instrução em Duas Fases:**\n\n**Fase 1: Extração Inicial**\nExtraia os números de CPF da coluna 'HISTÓRICO' e os valores monetários correspondentes da coluna 'ENTRADAS' presentes na imagem, associando-os linha a linha.\n\n**Fase 2: Verificação e Refinamento**\nApós a extração inicial, revise CUIDADOSAMENTE a imagem novamente e compare com os dados extraídos para garantir:\n1.  **Completude:** Todos os pares CPF/Valor visíveis e relevantes nas colunas especificadas foram extraídos? Não falta nenhum?\n2.  **Exatidão do CPF:** Cada número de CPF extraído é *idêntico* (incluindo pontos e hífen, se presentes na imagem) ao que consta na imagem?\n3.  **Exatidão do Valor:** Cada valor monetário extraído é *idêntico* (incluindo 'R$', ',', '.', e centavos, se presentes na imagem) ao que consta na coluna 'ENTRADAS' correspondente na imagem?\n\n**Resultado Final:**\nApós concluir a verificação e fazer as correções necessárias para garantir total precisão e completude, retorne APENAS a lista final e verificada, formatada com objetos contendo as chaves 'CPF' e 'Valor', seguindo este exemplo:\n[{'CPF': '123.456.789-00', 'Valor': 'R$ 100,00'}, ...]\n\n**Importante:**\n-   Não inclua NENHUMA explicação, texto introdutório/final, ou formatação adicional (como ```json```).\n-   Use aspas simples exclusivamente para o conteúdo interno das chaves e valores string (exemplo: 'CPF', 'Valor', '123.456.789-00', 'R$ 100,00').\n-   Retorne diretamente a lista [{...}, {...}] como resposta final.\n\n Caso não esteja no formato acima apenas identifique e extraia o CPF e o valor seguindo o modelo de resposta final.";
      
      // Log da requisição do OCR
      await saveLog('REQUISICAO_OCR_RENDIMENTO', {
        endpoint,
        request: {
          model,
          prompt,
          url: noCacheUrl
        },
        month,
        fileName,
        retryCount
      });
      
      // Criar um AbortController para controlar o timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 segundos de timeout
      
      try {
        // Fazer a requisição para o OCR com timeout
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'fastapi_live_f6d0d199fa86abaa4d1169983c5f3f66'
          },
          body: JSON.stringify({
            model,
            prompt,
            url,
            skipRevalidation: true // Disable CPF revalidation in the API
          }),
          signal: controller.signal
        });
        
        // Limpar o timeout se a resposta chegar antes
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          const errorText = await response.text();
          await saveLog('ERRO_OCR_RENDIMENTO', {
            endpoint,
            request: {
              model,
              prompt,
              url: noCacheUrl
            },
            statusCode: response.status,
            month,
            fileName,
            retryCount
          }, null, errorText);
          
          throw new Error(`Erro ao extrair informações do documento: ${response.status} - ${errorText}`);
        }
        
        const data = await response.json();
        
        // Registrar log da resposta bem-sucedida
        await saveLog('RESPOSTA_OCR_RENDIMENTO_SUCESSO', {
          endpoint,
          request: {
            model,
            prompt,
            url: noCacheUrl
          },
          month,
          fileName,
          retryCount
        }, data);
        
        // Verificar se o OCR retornou conteúdo válido
        if (!data || !data.content) {
          console.error('Resposta OCR sem conteúdo válido');
          throw new Error('Resposta OCR sem conteúdo válido');
        }
        
        return data.content;
      } catch (fetchError) {
        // Limpar o timeout se ocorrer um erro
        clearTimeout(timeoutId);
        
        // Verificar se foi um erro de timeout (AbortError) ou outro erro
        if (fetchError.name === 'AbortError' || fetchError.message === 'Failed to fetch') {
          await saveLog('TIMEOUT_OCR_RENDIMENTO', {
            endpoint,
            request: {
              model,
              prompt,
              url: noCacheUrl
            },
            month,
            fileName,
            retryCount,
            timeout: '60 segundos'
          });
          
          // Se já tentamos 2 vezes (retryCount = 0, 1), não tentar novamente
          if (retryCount < 2) {
            const nextRetryTime = retryCount === 0 ? 60 : 120;
            
            setProcessingDetails(prev => ({
              ...prev, 
              [month]: `Timeout na requisição OCR. Nova tentativa em ${nextRetryTime} segundos... (Tentativa ${retryCount + 1}/3)`
            }));
            
            // Aguardar o tempo definido antes de tentar novamente
            await new Promise(resolve => setTimeout(resolve, nextRetryTime * 1000));
            
            // Tentar novamente com retryCount incrementado
            return extractDocumentInfo(url, month, fileName, retryCount + 1);
          }
        }
        
        // Se não for um erro de timeout ou já tentamos 3 vezes, propagar o erro
        throw fetchError;
      }
    } catch (error) {
      console.error('Erro ao extrair informações do documento:', error);
      throw error;
    }
  };

  // Função para validar CPF
  const validarCPF = (cpf) => {
    // Remover caracteres não numéricos
    cpf = cpf.replace(/[^\d]/g, '');
    
    // Verificar se tem 11 dígitos
    if (cpf.length !== 11) {
      return false;
    }
    
    // Verificar se todos os dígitos são iguais (caso inválido)
    if (/^(\d)\1{10}$/.test(cpf)) {
      return false;
    }
    
    // Validar primeiro dígito verificador
    let soma = 0;
    for (let i = 0; i < 9; i++) {
      soma += parseInt(cpf.charAt(i)) * (10 - i);
    }
    let resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) {
      resto = 0;
    }
    if (resto !== parseInt(cpf.charAt(9))) {
      return false;
    }
    
    // Validar segundo dígito verificador
    soma = 0;
    for (let i = 0; i < 10; i++) {
      soma += parseInt(cpf.charAt(i)) * (11 - i);
    }
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) {
      resto = 0;
    }
    if (resto !== parseInt(cpf.charAt(10))) {
      return false;
    }
    
    return true;
  };

  // Função para formatar CPF
  const formatarCPF = (cpf) => {
    // Remover caracteres não numéricos
    cpf = cpf.replace(/[^\d]/g, '');
    
    // Retornar CPF formatado (XXX.XXX.XXX-XX)
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  // Função para converter valor monetário para número
  const converterValorParaNumero = (valorString) => {
    if (!valorString) return 0;
    
    // Remover 'R$' e espaços
    const valorLimpo = valorString.replace(/R\$|\s/g, '');
    
    // Formato brasileiro: 1.234,56 -> Converter para formato numérico 1234.56
    // Primeiro, remover todos os pontos (separadores de milhar)
    const semPontos = valorLimpo.replace(/\./g, '');
    
    // Depois, substituir a vírgula por ponto (separador decimal)
    const valorFormatado = semPontos.replace(',', '.');
    
    // Converter para número e garantir 2 casas decimais
    const valor = parseFloat(valorFormatado);
    
    // Retornar o valor com 2 casas decimais
    return parseFloat(valor.toFixed(2));
  };

  // Função para processar dados extraídos do OCR
  const processarDadosExtraidos = async (content, month, uploadResults) => {
    try {
      // Tentar avaliar o conteúdo como um array de objetos
      // Primeiro, remover qualquer formatação extra que possa estar presente
      const contentClean = content.trim();
      
      // Log para debug
      console.log(`Conteúdo extraído para ${month}:`, contentClean);
      
      let data;
      try {
        // Primeiro, tente avaliar como JSON
        if (contentClean.startsWith('[') && contentClean.endsWith(']')) {
          // Substituir aspas simples por aspas duplas para ser um JSON válido
          const jsonString = contentClean.replace(/'/g, '"');
          data = JSON.parse(jsonString);
        } else {
          // Se não for JSON, tente avaliar como expressão JavaScript
          // Isso é um risco de segurança, mas o conteúdo vem da nossa própria API
          // Uma alternativa seria usar uma abordagem baseada em regex
          data = eval(contentClean);
        }
      } catch (evalError) {
        console.error('Erro ao avaliar conteúdo:', evalError);
        throw new Error(`Não foi possível processar o conteúdo extraído: ${evalError.message}`);
      }
      
      if (!Array.isArray(data)) {
        throw new Error('O conteúdo extraído não é um array válido');
      }
      
      // Objetos para armazenar CPFs válidos e inválidos
      const cpfsValidos = [];
      const cpfsInvalidos = [];
      
      // Processar cada item extraído
      for (const item of data) {
        if (!item.CPF || !item.Valor) {
          console.warn('Item sem CPF ou Valor, ignorando:', item);
          continue;
        }
        
        // Limpar o CPF (remover caracteres não numéricos)
        const cpfLimpo = item.CPF.replace(/[^\d]/g, '');
        
        // Validar o CPF
        if (validarCPF(cpfLimpo)) {
          // Converter o valor para número
          const valorNumerico = converterValorParaNumero(item.Valor);
          
          // Adicionar à lista de CPFs válidos com o valor
          cpfsValidos.push({
            cpf: formatarCPF(cpfLimpo),
            valor: valorNumerico
          });
        } else {
          // Adicionar diretamente à lista de CPFs inválidos sem tentar revalidar
          cpfsInvalidos.push({
            cpf: item.CPF,
            valor: item.Valor,
            mes: months[month].name
          });
        }
      }
      
      return {
        rendimentos: cpfsValidos,
        cpfsInvalidos
      };
    } catch (error) {
      console.error('Erro ao processar dados extraídos:', error);
      throw error;
    }
  };

  // Função para processar CPFs corrigidos manualmente
  const processCorrectedCPFs = async () => {
    if (!cpfsCorrectedFile || !result) {
      setError("Nenhum arquivo de CPFs corrigidos selecionado ou não há resultado para processar.");
      return;
    }
    
    try {
      setProcessingCorrected(true);
      setSuccess(null);
      setError(null);
      setStillInvalidCpfs([]);
      
      // Ler o conteúdo do arquivo
      const fileReader = new FileReader();
      
      fileReader.onload = async (e) => {
        try {
          // Parsear o conteúdo do arquivo como JSON
          const correctedData = JSON.parse(e.target.result);
          
          if (!Array.isArray(correctedData)) {
            throw new Error("O arquivo deve conter um array de objetos com CPFs corrigidos.");
          }
          
          // Validar cada CPF corrigido
          const validCpfs = [];
          const invalidCpfs = [];
          
          for (const item of correctedData) {
            if (!item.cpf) {
              invalidCpfs.push({...item, motivo: "CPF não fornecido"});
              continue;
            }
            
            // Limpar o CPF (remover caracteres não numéricos)
            const cpfLimpo = item.cpf.replace(/[^\d]/g, '');
            
            // Validar o CPF
            if (validarCPF(cpfLimpo)) {
              // Converter o valor para número se necessário
              const valor = typeof item.valor === 'number' 
                ? item.valor 
                : converterValorParaNumero(item.valor);
              
              // Adicionar à lista de CPFs válidos
              validCpfs.push({
                cpf: formatarCPF(cpfLimpo),
                valor,
                mes: item.mes
              });
            } else {
              invalidCpfs.push({...item, motivo: "CPF inválido"});
            }
          }
          
          // Se não houver CPFs válidos, mostrar erro
          if (validCpfs.length === 0) {
            setError("Nenhum CPF válido encontrado no arquivo.");
            setStillInvalidCpfs(invalidCpfs);
            setProcessingCorrected(false);
            return;
          }
          
          // Agrupar CPFs válidos por mês
          const cpfsByMonth = {};
          
          for (const item of validCpfs) {
            // Encontrar o mês correspondente
            const monthKey = Object.keys(months).find(
              key => months[key].name.toLowerCase() === item.mes.toLowerCase()
            );
            
            if (monthKey) {
              if (!cpfsByMonth[monthKey]) {
                cpfsByMonth[monthKey] = [];
              }
              
              cpfsByMonth[monthKey].push({
                cpf: item.cpf,
                valor: item.valor
              });
            }
          }
          
          // Atualizar o resultado
          const updatedResult = {...result};
          
          // Adicionar CPFs válidos ao resultado
          for (const [month, cpfs] of Object.entries(cpfsByMonth)) {
            if (!updatedResult.rendimentos[month]) {
              updatedResult.rendimentos[month] = {
                rendimentos: []
              };
            }
            
            // Adicionar os CPFs corrigidos
            updatedResult.rendimentos[month].rendimentos = [
              ...updatedResult.rendimentos[month].rendimentos,
              ...cpfs
            ];
          }
          
          // Remover CPFs corrigidos da lista de inválidos
          const validCpfValues = validCpfs.map(item => item.cpf);
          updatedResult.cpfsInvalidos = updatedResult.cpfsInvalidos.filter(
            item => !validCpfValues.includes(formatarCPF(item.cpf.replace(/[^\d]/g, '')))
          );
          
          // Atualizar o resultado
          setResult(updatedResult);
          
          // Mostrar mensagem de sucesso
          setSuccess(`${validCpfs.length} CPF(s) corrigido(s) com sucesso! ${invalidCpfs.length > 0 ? `${invalidCpfs.length} CPF(s) ainda inválido(s).` : ''}`);
          
          // Armazenar CPFs ainda inválidos
          setStillInvalidCpfs(invalidCpfs);
          
          // Registrar log
          await saveLog('CORRECAO_MANUAL_CPFS', {
            totalCorrigidos: validCpfs.length,
            totalInvalidos: invalidCpfs.length,
            cpfsCorrigidos: validCpfs,
            cpfsAindaInvalidos: invalidCpfs
          });
          
        } catch (error) {
          console.error('Erro ao processar arquivo de CPFs corrigidos:', error);
          setError(`Erro ao processar arquivo: ${error.message}`);
        } finally {
          setProcessingCorrected(false);
        }
      };
      
      fileReader.onerror = () => {
        setError("Erro ao ler o arquivo.");
        setProcessingCorrected(false);
      };
      
      fileReader.readAsText(cpfsCorrectedFile);
      
    } catch (error) {
      console.error('Erro ao processar CPFs corrigidos:', error);
      setError(`Erro ao processar CPFs corrigidos: ${error.message}`);
      setProcessingCorrected(false);
    }
  };
  
  // Função para processar CPFs editados diretamente na interface
  const processEditedCPFs = async () => {
    if (!result || !editedCpfs || Object.keys(editedCpfs).length === 0) {
      setError("Não há CPFs editados para processar.");
      return;
    }
    
    try {
      setProcessingCorrected(true);
      setSuccess(null);
      setError(null);
      setStillInvalidCpfs([]);
      
      // Mapear os índices dos CPFs que foram editados
      const editedIndices = Object.keys(editedCpfs).map(index => parseInt(index));
      
      // Validar cada CPF editado
      const validCpfs = [];
      const invalidCpfs = [];
      const originalInvalidCpfs = [...result.cpfsInvalidos];
      
      // Processar apenas os CPFs que foram editados
      editedIndices.forEach(index => {
        const originalItem = originalInvalidCpfs[index];
        const editedCpf = editedCpfs[index];
        
        if (!editedCpf) {
          invalidCpfs.push({...originalItem, motivo: "CPF não fornecido"});
          return;
        }
        
        // Limpar o CPF (remover caracteres não numéricos)
        const cpfLimpo = editedCpf.replace(/[^\d]/g, '');
        
        // Validar o CPF
        if (validarCPF(cpfLimpo)) {
          // Converter o valor para número se necessário
          const valor = typeof originalItem.valor === 'number' 
            ? originalItem.valor 
            : converterValorParaNumero(originalItem.valor);
          
          // Adicionar à lista de CPFs válidos
          validCpfs.push({
            cpf: formatarCPF(cpfLimpo),
            valor,
            mes: originalItem.mes,
            originalIndex: index // Guardar o índice original para remoção posterior
          });
        } else {
          invalidCpfs.push({
            ...originalItem,
            cpf: editedCpf, // Usar o CPF editado, mesmo que ainda seja inválido
            motivo: "CPF inválido após edição"
          });
        }
      });
      
      // Se não houver CPFs válidos, mostrar erro
      if (validCpfs.length === 0) {
        setError("Nenhum CPF válido encontrado após edição.");
        setStillInvalidCpfs(invalidCpfs);
        setProcessingCorrected(false);
        return;
      }
      
      // Agrupar CPFs válidos por mês
      const cpfsByMonth = {};
      
      for (const item of validCpfs) {
        // Encontrar o mês correspondente
        const monthKey = Object.keys(months).find(
          key => months[key].name.toLowerCase() === item.mes.toLowerCase()
        );
        
        if (monthKey) {
          if (!cpfsByMonth[monthKey]) {
            cpfsByMonth[monthKey] = [];
          }
          
          cpfsByMonth[monthKey].push({
            cpf: item.cpf,
            valor: item.valor
          });
        }
      }
      
      // Atualizar o resultado
      const updatedResult = {...result};
      
      // Adicionar CPFs válidos ao resultado
      for (const [month, cpfs] of Object.entries(cpfsByMonth)) {
        if (!updatedResult.rendimentos[month]) {
          updatedResult.rendimentos[month] = {
            rendimentos: []
          };
        }
        
        // Adicionar os CPFs corrigidos
        updatedResult.rendimentos[month].rendimentos = [
          ...updatedResult.rendimentos[month].rendimentos,
          ...cpfs
        ];
      }
      
      // Criar uma nova lista de CPFs inválidos, excluindo os que foram corrigidos com sucesso
      const indicesParaRemover = validCpfs.map(item => item.originalIndex);
      const novosCpfsInvalidos = originalInvalidCpfs.filter((_, index) => !indicesParaRemover.includes(index));
      
      // Atualizar os CPFs inválidos que ainda não passaram na validação
      invalidCpfs.forEach(item => {
        const index = originalInvalidCpfs.findIndex(
          original => original.cpf === item.cpf && original.mes === item.mes
        );
        if (index !== -1 && !indicesParaRemover.includes(index)) {
          novosCpfsInvalidos[novosCpfsInvalidos.findIndex(
            cpf => cpf.cpf === originalInvalidCpfs[index].cpf && cpf.mes === originalInvalidCpfs[index].mes
          )] = item;
        }
      });
      
      // Atualizar a lista de CPFs inválidos no resultado
      updatedResult.cpfsInvalidos = novosCpfsInvalidos;
      
      // Atualizar o resultado
      setResult(updatedResult);
      
      // Mostrar mensagem de sucesso
      setSuccess(`${validCpfs.length} CPF(s) corrigido(s) com sucesso! ${invalidCpfs.length > 0 ? `${invalidCpfs.length} CPF(s) ainda inválido(s).` : ''}`);
      
      // Armazenar CPFs ainda inválidos
      setStillInvalidCpfs(invalidCpfs);
      
      // Limpar os CPFs editados e sair do modo de edição
      setEditedCpfs({});
      setEditingCpfs(false);
      
      // Registrar log
      await saveLog('CORRECAO_MANUAL_CPFS_INTERFACE', {
        totalCorrigidos: validCpfs.length,
        totalInvalidos: invalidCpfs.length,
        cpfsCorrigidos: validCpfs,
        cpfsAindaInvalidos: invalidCpfs
      });
      
    } catch (error) {
      console.error('Erro ao processar CPFs editados:', error);
      setError(`Erro ao processar CPFs editados: ${error.message}`);
    } finally {
      setProcessingCorrected(false);
    }
  };
  
  // Função para processar valores editados
  const processEditedValues = async () => {
    if (!result || !editedValues || Object.keys(editedValues).length === 0) {
      setError("Não há valores editados para processar.");
      return;
    }
    
    try {
      setProcessingEditedValues(true);
      setSuccess(null);
      setError(null);
      
      // Criar uma cópia do resultado atual
      const updatedResult = JSON.parse(JSON.stringify(result));
      
      // Processar os valores editados
      for (const key in editedValues) {
        // A chave está no formato "month:index" (ex: "janeiro:0")
        const [month, index] = key.split(':');
        const indexNum = parseInt(index);
        
        // Verificar se o mês e o índice são válidos
        if (
          updatedResult.rendimentos[month] && 
          updatedResult.rendimentos[month].rendimentos && 
          indexNum >= 0 && 
          indexNum < updatedResult.rendimentos[month].rendimentos.length
        ) {
          // Converter o valor editado para número
          let novoValor = editedValues[key];
          
          // Se o valor for uma string, converter para número
          if (typeof novoValor === 'string') {
            novoValor = converterValorParaNumero(novoValor);
          }
          
          // Atualizar o valor no resultado
          updatedResult.rendimentos[month].rendimentos[indexNum].valor = novoValor;
        }
      }
      
      // Atualizar o resultado
      setResult(updatedResult);
      
      // Mostrar mensagem de sucesso
      setSuccess(`${Object.keys(editedValues).length} valor(es) atualizado(s) com sucesso!`);
      
      // Limpar os valores editados e sair do modo de edição
      setEditedValues({});
      setEditingValuesMonth(null);
      
      // Registrar log
      await saveLog('EDICAO_VALORES_CPFS', {
        totalEditados: Object.keys(editedValues).length,
        valoresEditados: editedValues
      });
      
    } catch (error) {
      console.error('Erro ao processar valores editados:', error);
      setError(`Erro ao processar valores editados: ${error.message}`);
    } finally {
      setProcessingEditedValues(false);
    }
  };
  
  // Função para lidar com o input de arquivo de CPFs corrigidos
  const handleCorrectedFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
        setError("Por favor, selecione um arquivo JSON válido.");
        return;
      }
      
      setCpfsCorrectedFile(file);
      setError(null);
    }
  };
  
  // Função para baixar o template de CPFs inválidos
  const downloadInvalidCPFsTemplate = () => {
    if (!result || !result.cpfsInvalidos || result.cpfsInvalidos.length === 0) {
      setError("Não há CPFs inválidos para baixar.");
      return;
    }
    
    try {
      // Criar o conteúdo do arquivo
      const content = JSON.stringify(result.cpfsInvalidos, null, 2);
      
      // Criar um blob com o conteúdo
      const blob = new Blob([content], { type: 'application/json' });
      
      // Criar uma URL para o blob
      const url = URL.createObjectURL(blob);
      
      // Criar um elemento <a> para fazer o download
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cpfs-invalidos.json';
      
      // Adicionar o elemento ao DOM, clicar nele e removê-lo
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('Erro ao baixar template de CPFs inválidos:', error);
      setError(`Erro ao baixar template: ${error.message}`);
    }
  };
  
  return (
    <div className="container mx-auto">
      <header className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Link to={createPageUrl("Operations")}>
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <UserCog className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Rendimentos Recebidos {'>'} Pessoa Física</h1>
          </div>
        </div>
        <p className="text-gray-500">
          Esta ferramenta permite cadastrar rendimentos recebidos de pessoas físicas para declaração no Imposto de Renda. Cada documento será processado individualmente.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Gerenciamento de Rendimentos PF</CardTitle>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm mb-6">
                  <p className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    <span>{error}</span>
                  </p>
                </div>
              )}
              {success && (
                <div className="bg-green-100 text-green-700 rounded-md p-3 mb-6">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium">{success}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800">Arquivos por Mês:</h3>
                <div className="space-y-3">
                  {Object.entries(months).map(([month, monthData]) => (
                    <div 
                      key={month} 
                      className={`flex items-center justify-between border rounded-md p-3`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-100 rounded-md">
                          <Calendar className="h-6 w-6 text-gray-500" />
                        </div>
                        
                        <div>
                          <p className="font-medium text-sm">{monthData.name}</p>
                          {filesByMonth[month].length > 0 ? (
                            <div>
                              <p className="text-xs text-gray-500">
                                {filesByMonth[month].length} {filesByMonth[month].length === 1 ? 'arquivo' : 'arquivos'}
                              </p>
                              {filesByMonth[month].length > 0 && (
                                <div className="mt-1 max-h-16 overflow-y-auto">
                                  {filesByMonth[month].map(file => (
                                    <div key={file.id} className="flex justify-between items-center text-xs text-gray-500 mb-1">
                                      <span className="truncate max-w-[120px]">{file.name}</span>
                                      {uploadProgress[file.id] === 100 && (
                                        <CheckCircle2 className="h-3 w-3 text-green-500 ml-1" />
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500">Nenhum arquivo</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {filesByMonth[month].length > 0 && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-red-500 hover:bg-red-50"
                            onClick={() => removeAllFilesFromMonth(month)}
                            disabled={uploading}
                            title="Remover todos os arquivos"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                        
                        <input
                          ref={fileInputRefs[month]}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={(e) => handleFileInput(e, month)}
                          disabled={uploading}
                        />
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          className="flex items-center gap-1 text-blue-600"
                          onClick={() => handleButtonClick(month)}
                          disabled={uploading}
                        >
                          <Upload className="h-4 w-4" />
                          {filesByMonth[month].length > 0 ? 'Adicionar Arquivo' : 'Selecionar Arquivo'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Detalhes de processamento */}
              {Object.keys(processingDetails).length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-800 text-sm mb-6">
                  <p className="font-medium mb-2">Progresso do processamento:</p>
                  <ul className="space-y-1 max-h-40 overflow-y-auto">
                    {Object.entries(processingDetails).map(([key, value]) => (
                      <li key={key} className="flex items-center">
                        <Loader2 className="h-3 w-3 animate-spin mr-2" />
                        {value}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                {Object.values(filesByMonth).some(files => files.length > 0) && (
                  <Button 
                    type="button"
                    className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                    onClick={processAllDocuments}
                    disabled={processing || uploading}
                  >
                    {processing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <DollarSign className="h-4 w-4" />
                        Processar Arquivos Enviados
                        <span className="text-xs bg-white text-blue-600 rounded-full px-2 py-0.5 ml-1">
                          {Object.values(filesByMonth).reduce((total, files) => total + files.length, 0)}
                        </span>
                      </>
                    )}
                  </Button>
                )}
              </div>

              {/* Resultados do Processamento */}
              {processedDocuments.length > 0 && (
                <div className="mt-8">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-gray-800">Resultado do Processamento:</h3>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className={`${activeTab === 'upload' ? 'bg-blue-50 text-blue-600' : ''}`}
                        onClick={() => setActiveTab('upload')}
                      >
                        Upload
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className={`${activeTab === 'resultado' ? 'bg-blue-50 text-blue-600' : ''}`}
                        onClick={() => setActiveTab('resultado')}
                      >
                        Resultado
                      </Button>
                    </div>
                  </div>

                  {activeTab === 'upload' && (
                    <div className="space-y-2">
                      {processedDocuments.map((item, index) => (
                        <div 
                          key={index} 
                          className={`flex items-center justify-between p-3 rounded-md ${
                            item.status === 'concluido' 
                              ? 'bg-green-50 border border-green-200' 
                              : item.status === 'erro' 
                                ? 'bg-red-50 border border-red-200' 
                                : item.status === 'processando' 
                                  ? 'bg-blue-50 border border-blue-200' 
                                  : 'bg-gray-50 border border-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {item.status === 'concluido' ? (
                              <CheckCircle2 className="h-5 w-5 text-green-500" />
                            ) : item.status === 'erro' ? (
                              <AlertCircle className="h-5 w-5 text-red-500" />
                            ) : (
                              <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                            )}
                            
                            <div>
                              <p className="font-medium text-sm">{months[item.month].name}: {item.fileName}</p>
                              {item.status === 'concluido' && item.data && (
                                <p className="text-xs text-gray-600">
                                  {item.data.fonte_pagadora} - R$ {item.data.valor}
                                </p>
                              )}
                              {item.status === 'erro' && (
                                <p className="text-xs text-red-600">{item.mensagem}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {activeTab === 'resultado' && result && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.entries(result.rendimentos).map(([month, data]) => (
                          <div 
                            key={month}
                            className="bg-white border rounded-lg p-4 shadow-sm"
                          >
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-gray-800">{months[month].name}</h4>
                              <div className="flex items-center gap-2">
                                <DollarSign className={`h-5 w-5 text-${months[month].color}-500`} />
                                {editingValuesMonth === month ? (
                                  <Button
                                    size="sm"
                                    variant="default"
                                    className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white h-7 px-2 py-0"
                                    onClick={processEditedValues}
                                    disabled={processingEditedValues}
                                  >
                                    {processingEditedValues ? (
                                      <>
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                      </>
                                    ) : (
                                      <>
                                        <CheckCircle2 className="h-3 w-3" /> Rep
                                      </>
                                    )}
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex items-center gap-1 text-blue-600 border-blue-300 h-7 px-2 py-0"
                                    onClick={() => setEditingValuesMonth(month)}
                                  >
                                    <Pencil className="h-3 w-3" /> Edit
                                  </Button>
                                )}
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-500">Total de CPFs:</span>
                                <span className="text-sm font-medium">{data.rendimentos.length}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-500">Valor Total:</span>
                                <span className="text-sm font-medium text-green-600">
                                  R$ {data.rendimentos.reduce((acc, item) => acc + item.valor, 0).toFixed(2)}
                                </span>
                              </div>
                            </div>
                            
                            {data.rendimentos.length > 0 && (
                              <div className="mt-3 pt-3 border-t">
                                <h5 className="text-sm font-semibold mb-2">CPFs:</h5>
                                <div className="max-h-40 overflow-y-auto">
                                  {data.rendimentos.map((item, index) => (
                                    <div key={index} className="text-xs flex justify-between mb-1">
                                      <span>{item.cpf}</span>
                                      {editingValuesMonth === month ? (
                                        <input
                                          type="text"
                                          className="w-24 p-1 border rounded text-xs"
                                          defaultValue={
                                            editedValues[`${month}:${index}`] !== undefined
                                              ? editedValues[`${month}:${index}`]
                                              : item.valor.toFixed(2)
                                          }
                                          onChange={(e) => {
                                            const newEditedValues = {...editedValues};
                                            newEditedValues[`${month}:${index}`] = e.target.value;
                                            setEditedValues(newEditedValues);
                                          }}
                                          placeholder="Digite o valor"
                                        />
                                      ) : (
                                        <span className="text-green-600">R$ {item.valor.toFixed(2)}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Seção de CPFs Inválidos */}
                      {result.cpfsInvalidos && result.cpfsInvalidos.length > 0 && (
                        <div className="mt-6 border rounded-lg p-4 bg-orange-50">
                          <h3 className="text-lg font-semibold text-orange-800 mb-2">
                            CPFs Inválidos: {result.cpfsInvalidos.length}
                          </h3>
                          
                          <div className="flex flex-wrap gap-2 mb-4">
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex items-center gap-1 text-orange-600 border-orange-300"
                              onClick={() => setEditingCpfs(!editingCpfs)}
                            >
                              {editingCpfs ? (
                                <>
                                  <CheckCircle2 className="h-4 w-4" /> Finalizar Edição
                                </>
                              ) : (
                                <>
                                  <FileText className="h-4 w-4" /> Corrigir CPFs
                                </>
                              )}
                            </Button>
                            
                            {editingCpfs && (
                              <Button
                                size="sm"
                                variant="default"
                                className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white"
                                onClick={processEditedCPFs}
                                disabled={processingCorrected}
                              >
                                {processingCorrected ? (
                                  <>
                                    <Loader2 className="h-4 w-4 animate-spin" /> Processando...
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle2 className="h-4 w-4" /> Reprocessar
                                  </>
                                )}
                              </Button>
                            )}
                            
                            {!editingCpfs && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex items-center gap-1 text-blue-600 border-blue-300"
                                onClick={downloadInvalidCPFsTemplate}
                              >
                                <Download className="h-4 w-4" /> Baixar Lista
                              </Button>
                            )}
                          </div>
                          
                          {/* Table of invalid CPFs */}
                          <div className="mt-2 overflow-y-auto max-h-80 bg-white border rounded-lg">
                            <table className="w-full text-sm text-left">
                              <thead className="bg-gray-50 text-gray-700">
                                <tr>
                                  <th scope="col" className="px-4 py-2 font-medium">CPF</th>
                                  <th scope="col" className="px-4 py-2 font-medium">Valor</th>
                                  <th scope="col" className="px-4 py-2 font-medium">Mês</th>
                                </tr>
                              </thead>
                              <tbody>
                                {result.cpfsInvalidos.map((item, index) => (
                                  <tr key={index} className="border-t">
                                    <td className="px-4 py-2">
                                      {editingCpfs ? (
                                        <input
                                          type="text"
                                          className="w-full p-1 border rounded"
                                          defaultValue={editedCpfs[index] || item.cpf}
                                          onChange={(e) => {
                                            const newEditedCpfs = {...editedCpfs};
                                            newEditedCpfs[index] = e.target.value;
                                            setEditedCpfs(newEditedCpfs);
                                          }}
                                          placeholder="Digite o CPF correto"
                                        />
                                      ) : (
                                        item.cpf
                                      )}
                                    </td>
                                    <td className="px-4 py-2">R$ {typeof item.valor === 'number' ? item.valor.toFixed(2) : item.valor}</td>
                                    <td className="px-4 py-2">{item.mes}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          
                          {/* Section for CPFs that are still invalid after correction */}
                          {stillInvalidCpfs.length > 0 && (
                            <div className="mt-4">
                              <h5 className="text-sm font-medium text-red-700 mb-2">
                                CPFs ainda inválidos após correção ({stillInvalidCpfs.length}):
                              </h5>
                              <div className="max-h-40 overflow-y-auto bg-white border rounded p-2">
                                <pre className="text-xs">{JSON.stringify(stillInvalidCpfs, null, 2)}</pre>
                              </div>
                            </div>
                          )}
                          
                          <div className="mt-4 border-t pt-4">
                            <h4 className="text-md font-medium mb-2">Corrigir CPFs Manualmente</h4>
                            <p className="text-sm text-gray-600 mb-3">
                              1. Clique em "Corrigir CPFs" para editar os CPFs inválidos<br />
                              2. Edite os números de CPF diretamente na tabela<br />
                              3. Clique em "Reprocessar" para validar os CPFs corrigidos
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle>Informações</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800">O que é isso?</h3>
                <p className="text-sm text-gray-600">
                  Esta ferramenta permite cadastrar rendimentos recebidos de pessoas físicas para declaração no Imposto de Renda Pessoa Física (IRPF).
                </p>
                
                <h3 className="font-semibold text-gray-800">Como usar</h3>
                <ol className="text-sm text-gray-600 space-y-2 list-decimal ml-4">
                  <li>Selecione um arquivo para cada mês que deseja declarar rendimentos</li>
                  <li>Clique em "Processar Rendimentos" para iniciar o processamento dos arquivos</li>
                  <li>Após o processamento, você verá o resultado organizado por mês</li>
                  <li>Use o botão "Copiar" para copiar o JSON ou "Baixar" para fazer o download do arquivo</li>
                </ol>
                
                <h3 className="font-semibold text-gray-800">Tipos de documentos aceitos</h3>
                <ul className="text-sm text-gray-600 space-y-1 list-disc ml-4">
                  <li>Informes de rendimentos</li>
                  <li>Comprovantes de pagamento</li>
                  <li>Recibos de prestação de serviços</li>
                  <li>Outros documentos relacionados a rendimentos de pessoa física</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {result && (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-800">JSON de Saída:</h3>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="flex items-center gap-1 text-blue-600"
                onClick={copyResultToClipboard}
              >
                <Copy className="h-4 w-4" /> Copiar
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex items-center gap-1 text-green-600"
                onClick={downloadJsonFile}
              >
                <Download className="h-4 w-4" /> Baixar JSON
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex items-center gap-1 text-purple-600"
                onClick={downloadResultAsPDF}
              >
                <FileDown className="h-4 w-4" /> Baixar PDF
              </Button>
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg overflow-x-auto">
            <pre className="text-sm text-gray-900">
              {JSON.stringify({ 
                rendimentos: Object.fromEntries(
                  Object.entries(result.rendimentos).map(([month, data]) => [
                    month, 
                    { rendimentos: data.rendimentos }
                  ])
                )
              }, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
